import clsx from 'clsx';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FakeLink } from '@/core/FakeLink';
import { SelectItemWrapper } from '@/core/SelectItemWrapper.organism';
import { sqWorkbookStore } from '@/core/core.stores';
import { useAllTrendStoreItems } from '@/core/hooks/useAllTrendStoreItems.hook';
import { decorate } from '@/trend/trendViewer/itemDecorator.utilities';
import { DETAILS_PANE_ITEM_TYPES } from '@/trendData/trendData.constants';
import { base64guid, validateGuid } from '@/utilities/guid.utilities';
import { errorToast } from '@/utilities/toast.utilities';
import { addItemType, decorateItemWithProperties, isStepSignal, isStringSeries } from '@/utilities/utilities';
import { AddToDisplayPane } from '@/workbook/AddToDisplayPane.molecule';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { faPlus, faSearch } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { fcAllItems, fcScalar } from '@seeqdev/icons/seeq';
import { Button, InputGroup } from '@seeqdev/qomponents';
import type { BaseItem } from '@/trendData/trendData.types';
import type { PermissionsV1 } from '@seeqdev/api/model/PermissionsV1';

// Search previews use generated PermissionsV1 (optional booleans); BaseItem requires all three.
export type SelectableItem = Omit<Partial<BaseItem>, 'effectivePermissions'> & {
  id: string;
  effectivePermissions?: PermissionsV1;
  selectGroup?: 'pinned' | 'recentlyAccessed';
  calculationType?: string;
  swapSourceId?: string;
  interpolationMethod?: string | { value?: string | null } | null;
  // Set by decorateItemWithProperties when includeMetadata fetches the full item
  signalMetadata?: { valueUnitOfMeasure?: string };
  [key: string]: unknown;
};

// Type for formula parameter items that includes formula-specific properties
type FormulaParameterItem = SelectableItem & {
  identifier: string;
  item: SelectableItem | string;
};

const isSelectableItem = (value: unknown): value is SelectableItem =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isDetailsPaneSupportedType = (itemType: string | undefined): boolean =>
  !_.isNil(itemType) && _.includes(DETAILS_PANE_ITEM_TYPES, itemType);

const decorateIfSupported = (item: SelectableItem) =>
  isDetailsPaneSupportedType(item.itemType) ? decorate({ items: item }) : item;

interface SelectItemIF {
  // true if the select can be toggled between a text input field and a select.
  textEntryAllowed?: boolean;
  textPlaceholder?: string;
  textToggleDisabled?: boolean;
  selectPlaceholder?: string;
  excludeStoreItems?: boolean;
  excludeWorkbookItems?: boolean;
  // true does not automatically select even if there's only one item
  disableAutoSelect?: boolean;
  showAddToDisplayPane?: boolean;
  // true to include metadata, or a function that takes the selected item and returns whether to fetch metadata
  includeMetadata?: boolean | ((item: SelectableItem) => boolean);
  excludeStringSignals?: boolean;
  includeOnlyStringSignals?: boolean;
  includeOnlyStepSignals?: boolean;
  isMultipleSelect?: boolean;
  selectedItemId?: string | string[];
  excludedIds?: string[];
  additionalItems?: SelectableItem[];
  // to limit items to only certain item types
  itemTypes?: string[];
  // index used to set the threshold in scorecard
  thresholdLevel?: string;
  // used by formula, the name
  identifier?: string;
  // used by formula, index
  paramIndex?: string;
  items?: SelectableItem[];
  allowClear?: boolean;
  extraClassNames?: string;
  // for Asset Groups we need to compare the items to the group as well as the item ids will be the same for asset
  // column items
  selectByIdAndGroup?: boolean;
  // Determines if the select element is allowed to wrap when the name is long
  noWrap?: boolean;
  onSelect: (selectedItemOrValue: SelectableItem | string | null, level?: number | string) => void;
  onRemove?: (selectedItem: SelectableItem) => void;
  // called when input is toggled from text input to signal select or the other way around. used for formBuilder
  onToggle?: () => void;
  isGrouped?: boolean;
  dataTestId?: string;
  addAllEnabled?: boolean;
  removeAllEnabled?: boolean;
  stopPropagation?: boolean;
  testId?: string;
  showError?: boolean;
  closeMenuOnSelect?: boolean;
  onRemoveAll?: () => void;
  onAddAllClick?: () => void;
  showOnlyCalculatedItems?: boolean;
  outputItemId?: string;
  showSearchButton?: boolean;
  showLane?: boolean;
  controlClassNames?: string;
  onSearchButtonClick?: () => void;
  refetchSelectedItem?: boolean;
  fetchMissingSelectedItem?: boolean;
  onAddItemsClick?: () => void;
  addItemsLabel?: string;
  addItemsEnabled?: boolean;
  isDisabled?: boolean;
}

/** Select dropdown for searching for and selecting Seeq Items */
export const SelectItem = (props: SelectItemIF) => {
  const { t } = useTranslation();

  const {
    textEntryAllowed = false,
    textPlaceholder,
    textToggleDisabled,
    selectPlaceholder,
    disableAutoSelect,
    showAddToDisplayPane = true,
    selectedItemId,
    onRemove = _.noop,
    onRemoveAll,
    onSelect,
    isMultipleSelect = false,
    excludeStoreItems,
    excludeWorkbookItems,
    additionalItems,
    excludeStringSignals,
    includeOnlyStringSignals = false,
    includeOnlyStepSignals = false,
    itemTypes,
    includeMetadata,
    identifier,
    thresholdLevel,

    allowClear = false,
    onToggle,
    extraClassNames,
    noWrap = false,
    selectByIdAndGroup = false,
    isGrouped = true,
    dataTestId = '',
    addAllEnabled = false,
    removeAllEnabled = false,
    stopPropagation = false,
    testId = '',
    showError = false,
    closeMenuOnSelect = true,
    onAddAllClick,
    showOnlyCalculatedItems,
    outputItemId,
    showSearchButton = false,
    showLane = false,
    controlClassNames,
    onSearchButtonClick,
    refetchSelectedItem = false,
    fetchMissingSelectedItem = true,
    onAddItemsClick,
    addItemsLabel,
    addItemsEnabled = false,
    isDisabled = false,
  } = props;
  const isMountedRef = React.useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!_.isArray(selectedItemId)) {
      const validItemSelected = validateGuid(selectedItemId);
      if (textEntryAllowed) {
        setDisplayTextEntry(!validItemSelected);
        if (!validItemSelected) {
          setTextInputText(selectedItemId ?? '');
        }
      }
    }
  }, [selectedItemId]);

  const [loadingMetadata, setLoadingMetadata] = useState<boolean>(false);
  const [displayTextEntry, setDisplayTextEntry] = useState<boolean>(false);
  const [displayItemsStepSignals, setDisplayItemsStepSignals] = useState<SelectableItem[]>([]);
  const [previousValue, setPreviousValue] = useState<string | string[] | undefined>('');

  // This is a bit of a hack to work around the fact that text input is too slow when running system tests.
  // To prevent errors, we store values from ongoing typing in the state and only update the store when the onBlur
  // is triggered. Once all forms use FormBuilder the Form can be used to store the state and we won't need to keep
  // values in local state.
  const selectedItemIdString = !_.isArray(selectedItemId) ? selectedItemId : undefined;
  const textInputValue: string =
    validateGuid(selectedItemIdString) || _.isUndefined(selectedItemIdString)
      ? ''
      : ((selectedItemIdString as string) ?? '');
  const [textInputText, setTextInputText] = useState<string>(textInputValue);
  const cancellationGroup = `selectItemMetadata_${base64guid()}`;

  if (textEntryAllowed && isMultipleSelect) {
    throw new Error('Item Select can not be isMultipleSelect and textEntryAllowed');
  }

  const trendItems: SelectableItem[] = useAllTrendStoreItems();

  const allItems: SelectableItem[] = _.concat(trendItems, additionalItems || []) || [];

  const excludedIds: string[] = _.chain(allItems)
    .flatMap((items) => _.chain(items).filter('swapSourceId').map('swapSourceId').value())
    .concat(props.excludedIds || [])
    .compact()
    .value();

  const displayItems: SelectableItem[] = excludeStoreItems
    ? (additionalItems ?? [])
    : _.chain(allItems)
        .concat(
          excludeWorkbookItems
            ? []
            : _.map(sqWorkbookStore.pinned, (item) => ({
                ...item,
                selectGroup: 'pinned',
              })),
        )
        .concat(
          excludeWorkbookItems
            ? []
            : _.map(sqWorkbookStore.recentlyAccessed, (item) => ({
                ...item,
                selectGroup: 'recentlyAccessed',
              })),
        )
        .map((item) => addItemType(item))
        .map((item) => decorateIfSupported(item))
        .reject((item) => (excludeStringSignals ? isStringSeries(item) : false))
        .reject((item) => (includeOnlyStringSignals ? !isStringSeries(item) : false))
        .reject((item) => _.includes(excludedIds, item.id))
        .reject(
          (item) =>
            showOnlyCalculatedItems &&
            (item.calculationType === undefined || item.calculationType === 'periodic-condition'),
        )
        .filter((item) => (_.isEmpty(itemTypes) ? true : _.includes(_.castArray(itemTypes), item.itemType)))
        .uniqBy('id')
        .value();

  useEffect(() => {
    if (!selectedItemId && displayItems?.length === 1 && !allowClear && !disableAutoSelect) {
      onItemSelect(displayItems[0]);
    }
  }, [displayItems]);

  useEffect(() => {
    if (includeOnlyStepSignals) {
      const itemsWithoutInterpolation: SelectableItem[] = _.reject(displayItems, (item) => item?.interpolationMethod);
      _.chain(itemsWithoutInterpolation)
        .map((item) => isStepSignal(item?.id))
        .thru((results) => Promise.all(results))
        .value()
        .then((responses) => {
          const stepSignals: SelectableItem[] = [];
          _.forEach(displayItems, (item, index) => {
            const interpolation = item?.interpolationMethod;
            const isStep =
              (typeof interpolation === 'object' && interpolation?.value === 'Step') || interpolation === 'Step';
            if (isStep) {
              stepSignals.push(displayItems[index]);
            }
          });
          _.forEach(responses, (response, index) => {
            if (response) {
              stepSignals.push(itemsWithoutInterpolation[index]);
            }
          });
          setDisplayItemsStepSignals(stepSignals);
        });
    }
  }, []);

  const toggleTextEntryDisplay = () => {
    if (_.isFunction(onToggle)) {
      onToggle();
    }
    const previouslyEntered = previousValue;
    const toggleToDisplayText = !displayTextEntry;

    setPreviousValue(selectedItemId);

    if (toggleToDisplayText) {
      onItemSelect(previouslyEntered as string);
    } else {
      const foundItem = _.find(displayItems, { id: previouslyEntered });
      onItemSelect(foundItem ?? null);
    }

    setDisplayTextEntry(toggleToDisplayText);
  };

  const onItemSelect = (item: SelectableItem | SelectableItem[] | string | null | undefined) => {
    if (!_.isFunction(onSelect)) {
      return;
    }

    if (_.isNil(item)) {
      if (allowClear) {
        onSelect(null);
      }
      return;
    }

    // Retrieving metadata is not supported if multiple selection is enabled or if item is a text value or undefined
    if (isMultipleSelect) {
      if (Array.isArray(item)) {
        const selectableItems = item.filter((obj): obj is SelectableItem => _.has(obj, 'name'));
        if (selectableItems.length > 0) {
          onSelect(selectableItems[0]);
        }
      }
      return;
    }

    // At this point, isMultipleSelect is false, but item could still be an array from SelectItemWrapper (e.g., Add All
    // functionality wraps items in arrays). We need to handle the empty array case to avoid undefined access.
    if (Array.isArray(item) && item.length === 0) {
      if (allowClear) {
        onSelect(null);
      }
      return;
    }

    const singleItem: SelectableItem | string = Array.isArray(item) ? item[0] : item;

    // Determine whether to fetch metadata: if includeMetadata is a function, call it with the item
    const shouldFetchMetadata =
      !_.isString(singleItem) &&
      includeMetadata &&
      isSelectableItem(singleItem) &&
      (typeof includeMetadata === 'function' ? includeMetadata(singleItem) : true);

    if (!shouldFetchMetadata) {
      if (textEntryAllowed) {
        onSelect(singleItem, thresholdLevel);
      } else if (identifier) {
        // formula - create a formula parameter object with proper typing
        const formulaItem: FormulaParameterItem = { identifier, item: singleItem, id: '' };
        onSelect(formulaItem);
      } else {
        onSelect(singleItem);
      }
    } else {
      isMountedRef.current && setLoadingMetadata(true);
      sqItemsApi
        .getItemAndAllProperties({ id: singleItem.id }, { cancellationGroup })
        .then(({ data: fetchedItem }) => {
          const decoratedItem = fetchedItem ? decorateItemWithProperties(fetchedItem) : singleItem;
          textEntryAllowed ? onSelect(decoratedItem, thresholdLevel) : onSelect(decoratedItem);
        })
        .catch((e) => {
          errorToast({ httpResponseOrError: e });
        })
        .finally(() => {
          isMountedRef.current && setLoadingMetadata(false);
        });
    }
  };

  const setThreshold = (event: React.ChangeEvent<HTMLInputElement>) => setTextInputText(event.target.value);
  const updateThresholdInStore = (event: React.FocusEvent<HTMLInputElement>) =>
    onSelect(event.target.value, thresholdLevel);

  const textInputTooltip = textToggleDisabled ? 'VALUE_SEARCH.STRING_SIGNAL_TOOLTIP' : 'USE_ITEM_SELECTOR';

  const selectedItem = isMultipleSelect
    ? Array.isArray(selectedItemId)
      ? _.difference(selectedItemId, excludedIds)
      : []
    : _.find(displayItems, { id: selectedItemId });

  const [fetchedSelectedItem, setFetchedSelectedItem] = useState<SelectableItem | undefined>(undefined);

  useEffect(() => {
    if (
      fetchMissingSelectedItem &&
      selectedItemId &&
      (!fetchedSelectedItem || refetchSelectedItem) &&
      validateGuid(selectedItemId) &&
      _.isUndefined(selectedItem)
    ) {
      sqItemsApi
        .getItemAndAllProperties({ id: selectedItemId as string })
        .then(({ data }) => {
          setFetchedSelectedItem(decorateIfSupported(addItemType(data)) as SelectableItem);
        })
        // The ID is malformed, or is otherwise a string, so ignore errors
        .catch(_.noop);
    }
  }, [selectedItemId, fetchMissingSelectedItem, refetchSelectedItem, selectedItem]);

  const effectiveSelectedItem = selectedItem ?? fetchedSelectedItem;
  const effectiveSelectedItemType = isSelectableItem(effectiveSelectedItem)
    ? effectiveSelectedItem.itemType
    : undefined;
  const isDisplayPaneSupportedType =
    _.isNil(effectiveSelectedItemType) || isDetailsPaneSupportedType(effectiveSelectedItemType);
  const displayAddToDisplayPane =
    showAddToDisplayPane &&
    !textEntryAllowed &&
    !_.isEmpty(selectedItemId) &&
    !isMultipleSelect &&
    isDisplayPaneSupportedType;

  const renderTextInputToggle = (
    <Button
      variant="no-border"
      tooltip={t(displayTextEntry ? textInputTooltip : 'USE_VALUE_ENTRY')}
      icon={displayTextEntry ? fcScalar : fcAllItems}
      testId="item-select-toggle"
      onClick={toggleTextEntryDisplay}
      extraClassNames={clsx('flexRowContainer tw:items-center tw:justify-center', {
        disabledBehavior: textToggleDisabled,
      })}
      disabled={textToggleDisabled}
    />
  );

  const renderSelect = !displayTextEntry && (
    <InputGroup
      field={
        <SelectItemWrapper
          selectPlaceholder={selectPlaceholder}
          selected={selectedItem ?? fetchedSelectedItem}
          isMultipleSelect={isMultipleSelect}
          items={_.concat(
            includeOnlyStepSignals ? displayItemsStepSignals : displayItems,
            fetchedSelectedItem ? [fetchedSelectedItem] : [],
          )}
          onChange={onItemSelect}
          onRemove={onRemove}
          loadingMetadata={loadingMetadata}
          allowClear={allowClear}
          disableAutoSelect={disableAutoSelect}
          noWrap={noWrap || textEntryAllowed}
          selectByIdAndGroup={selectByIdAndGroup}
          isGrouped={isGrouped}
          addAllEnabled={addAllEnabled}
          stopPropagation={stopPropagation}
          testId={`${testId}_select`}
          showError={showError}
          showLane={showLane}
          extraClassNames="btrr0 bbrr0"
          closeMenuOnSelect={closeMenuOnSelect}
          onAddAllClick={onAddAllClick}
          controlClassNames={controlClassNames}
          isDisabled={isDisabled}
        />
      }
      extraClassNames="no-border forceZeroPadding"
      append={[
        textEntryAllowed
          ? {
              variant: 'element',
              element: renderTextInputToggle,
            }
          : undefined,
        showSearchButton
          ? {
              variant: 'button',
              buttonProps: {
                icon: faSearch,
                iconStyle: 'theme',
                testId: `${testId}-search`,
                onClick: onSearchButtonClick,
              },
            }
          : undefined,
      ]}
    />
  );

  const renderTextInput = textEntryAllowed && displayTextEntry && (
    <InputGroup
      type="text"
      value={textInputText}
      onChange={setThreshold}
      onBlur={updateThresholdInStore}
      testId="item-select-text-input"
      extraClassNames={clsx(extraClassNames)}
      size="sm"
      placeholder={t(textPlaceholder ? textPlaceholder : 'ENTER_VALUE')}
      append={[
        textEntryAllowed
          ? {
              variant: 'element',
              element: renderTextInputToggle,
            }
          : undefined,
      ]}
    />
  );

  const removeAllLink = (
    <div className="flexColumnContainer flexAlignBottom tw:pt-sq-5 tw:pb-sq-5">
      <FakeLink
        testId="removeAllLink"
        onClick={() => {
          if (onRemoveAll) {
            onRemoveAll();
          } else {
            _.compact(displayItems).forEach((item) => onRemove(item));
          }
        }}>
        {t('DETAILS_SELECTION.REMOVE_ALL')}
      </FakeLink>
    </div>
  );

  const addItemsButton = (
    <div className="flexColumnContainer flexAlignBottom tw:pt-sq-5 tw:pb-sq-5">
      <Button
        testId="addItemsButton"
        icon={faPlus}
        variant="theme"
        size="xs"
        label={addItemsLabel!}
        onClick={onAddItemsClick!}
      />
    </div>
  );

  return (
    <div>
      <div
        className={clsx('flexColumnContainer tw:flex-1', textEntryAllowed ? 'textEntryAllowed' : 'tw:items-center')}
        data-testid={`itemSelect${dataTestId}`}>
        {renderTextInput}
        {renderSelect}
        {displayAddToDisplayPane && (
          <AddToDisplayPane itemId={selectedItemId as string} outputItemId={outputItemId} tooltipPlacement="left" />
        )}
      </div>
      <div className="tw:flex tw:items-center tw:justify-end tw:gap-2">
        {addItemsEnabled && addItemsLabel && onAddItemsClick && addItemsButton}
        {removeAllEnabled && removeAllLink}
      </div>
    </div>
  );
};

export default SelectItem;
